/**
 * SuccessPage Component
 * 
 * Payment confirmation page displayed after successful payment.
 * 
 * Task: T057
 * 
 * Props:
 * - requestId: Payment request ID
 * - paymentMethodId: Payment method ID from backend
 * - timestamp: Payment completion timestamp
 */

import React from 'react';
import { Card, CardBody, Typography, Button } from '@material-tailwind/react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const SuccessPage = ({ requestId, paymentMethodId, timestamp }) => {
  const formattedTimestamp = timestamp 
    ? new Date(timestamp).toLocaleString('en-US', {
        dateStyle: 'full',
        timeStyle: 'long',
      })
    : new Date().toLocaleString('en-US', {
        dateStyle: 'full',
        timeStyle: 'long',
      });

  return (
    <div className="max-w-md mx-auto">
      <Card className="shadow-lg">
        <CardBody className="text-center">
          {/* Success icon */}
          <div className="flex justify-center mb-6">
            <CheckCircleIcon className="h-20 w-20 text-green-500" />
          </div>

          {/* Success message */}
          <Typography variant="h3" color="green" className="mb-4">
            Payment Method Saved!
          </Typography>

          <Typography variant="lead" color="blue-gray" className="mb-6">
            Your payment method has been securely saved
          </Typography>

          {/* Payment details */}
          <div className="bg-blue-gray-50 rounded-lg p-4 mb-6 text-left">
            <Typography variant="small" color="blue-gray" className="font-semibold mb-2">
              Payment Method Details
            </Typography>

            {requestId && (
              <div className="mb-2">
                <Typography variant="small" color="blue-gray" className="opacity-70">
                  Request ID:
                </Typography>
                <Typography variant="small" color="blue-gray" className="font-mono break-all">
                  {requestId}
                </Typography>
              </div>
            )}

            {paymentMethodId && (
              <div className="mb-2">
                <Typography variant="small" color="blue-gray" className="opacity-70">
                  Payment Method ID:
                </Typography>
                <Typography variant="small" color="blue-gray" className="font-mono break-all">
                  {paymentMethodId}
                </Typography>
              </div>
            )}

            <div>
              <Typography variant="small" color="blue-gray" className="opacity-70">
                Completed:
              </Typography>
              <Typography variant="small" color="blue-gray">
                {formattedTimestamp}
              </Typography>
            </div>
          </div>

          {/* Confirmation message */}
          <Typography variant="paragraph" color="blue-gray" className="mb-6">
            A confirmation email has been sent to the email address associated with this request.
          </Typography>

          {/* Close button */}
          <Button
            size="lg"
            className="w-full bg-fotl-purple hover:bg-fotl-purple-dark"
            onClick={() => window.close()}
          >
            Close Window
          </Button>

          <Typography variant="small" color="blue-gray" className="mt-4 opacity-60">
            You can safely close this window.
          </Typography>
        </CardBody>
      </Card>
    </div>
  );
};

export default SuccessPage;
